import React from 'react';
import {
  getProjectFieldSuggestions,
  getProjectFieldSuggestionsHint,
} from '@scale/shared/textcollection/suggestions';
import {
  TextCollectionField,
  TextCollectionSuggestionSource,
} from '@scale/shared/types/params/textcollection';
import { TextCollectionAnnotation } from '@scale/shared/types/response/textcollection';
import { TextCollectionSuggestionValue } from '@scale/shared/types/textCollection';

interface SuggestionsProps {
  suggestionSources?: TextCollectionSuggestionSource[];
  children?: React.ReactNode;
}

interface SuggestionsState {
  suggestionSources?: TextCollectionSuggestionSource[];
  getFieldSuggestions: (
    field: TextCollectionField,
    annotations: Record<string, TextCollectionAnnotation>,
  ) => TextCollectionSuggestionValue[];
  getFieldSuggestionsHint: (field: TextCollectionField) => string | undefined;
}

const SuggestionsContext = React.createContext<SuggestionsState | undefined>(undefined);

export const SuggestionsProvider: React.FC<SuggestionsProps> = ({
  suggestionSources,
  children,
}) => {
  const getFieldSuggestions = React.useCallback(
    (field: TextCollectionField, annotations: Record<string, TextCollectionAnnotation>) => {
      if (suggestionSources) {
        return getProjectFieldSuggestions(suggestionSources, field, annotations);
      } else {
        return [];
      }
    },
    [suggestionSources],
  );
  const getFieldSuggestionsHint = React.useCallback(
    (field: TextCollectionField) => {
      if (suggestionSources) {
        return getProjectFieldSuggestionsHint(suggestionSources, field);
      }
      return undefined;
    },
    [suggestionSources],
  );
  const suggestionsState: SuggestionsState = {
    suggestionSources,
    getFieldSuggestions,
    getFieldSuggestionsHint,
  };
  return (
    <SuggestionsContext.Provider value={suggestionsState}>{children}</SuggestionsContext.Provider>
  );
};

export function useSuggestions(): SuggestionsState {
  const context = React.useContext(SuggestionsContext);
  if (!context) {
    throw new Error('useSuggestions must be called within a SuggestionsProvider');
  }
  return context;
}
